// Phase-04 data seam for the Squad module (no Phase-02 hook existed — Squad reads
// leagueTables + players directly). Pure lookups grounded in js/renderers-squads.js.
// At the Part-2 cutover these become a team-roster endpoint call adapted to the same
// ProtoSquadPlayer shape.

import type { ProtoSquadPlayer } from "@catbento/shared";
import type { CatalystSnapshot } from "./snapshot.js";
import { getLeagueIdNames, seasonSortKey, type CompetitionId } from "./league.js";
import { getPlayerShortName, comparePlayersByName } from "./player-utils.js";
import { playerIdOf } from "./players.js";

/**
 * Team names for a competition, from its NEWEST captured league-table season
 * (sorted). Derived from the clean standings — NOT players' own team/competition
 * fields — because a few raw players carry a wrong competition label, and older
 * seasons include clubs no longer in the competition (renderers-squads.js:21).
 */
export function getSquadTeams(data: CatalystSnapshot, competitionId: CompetitionId): string[] {
  const competition = getLeagueIdNames(data).get(competitionId);
  if (!competition) return [];
  const tables = data.leagueTables ?? {};
  const seasons = Object.keys(tables[competition] ?? {}).sort((a, b) => seasonSortKey(b) - seasonSortKey(a));
  const newest = seasons[0];
  const block = newest ? tables[competition]?.[newest] : null;
  return block ? block.table.map((t) => t.name).sort() : [];
}

/**
 * A team's roster in a competition — players whose team AND competition match,
 * sorted by name, narrowed to the Squad row fields (renderers-squads.js:178).
 */
export function getSquad(data: CatalystSnapshot, competitionId: CompetitionId, team: string): ProtoSquadPlayer[] {
  // Players carry their league as a name, so match on the snapshot's name for this id.
  const competition = getLeagueIdNames(data).get(competitionId);
  if (!competition) return [];
  return Object.values(data.players)
    .filter((p) => p.team === team && p.competition === competition)
    .sort(comparePlayersByName)
    .map((p) => ({
      playerId: playerIdOf(p),
      name: p.name ?? "",
      shortName: getPlayerShortName(p),
      positionTemplate: p.positionTemplate ?? null,
      country: p.country ?? null,
      dob: p.dob ?? null,
      hopsRating: p.hopsRating && typeof p.hopsRating.rating === "number" ? { rating: p.hopsRating.rating } : null,
    }));
}
